import {
  NODE_LABEL_VALUES,
  RELATIONSHIP_PREFIXES,
  STATIC_RELATIONSHIPS,
} from '@/types';

/**
 * Validation utilities for user input with security focus
 *
 * This module provides comprehensive validation functions that prioritize security
 * and prevent common vulnerabilities including username enumeration, reserved name
 * conflicts, and input sanitization.
 */

/**
 * Result type for validation operations
 */
export interface ValidationResult {
  /** Whether the input is valid */
  isValid: boolean;
  /** Array of error messages if validation fails */
  errors: string[];
  /** Optional warnings that don't prevent submission */
  warnings?: string[];
}

/**
 * Reserved usernames that should be rejected to prevent system conflicts
 * and security issues. Includes common system accounts, service accounts,
 * and names that could be used for social engineering.
 */
const RESERVED_USERNAMES = [
  // System accounts
  'root',
  'admin',
  'administrator',
  'system',
  'daemon',
  'nobody',
  'www',
  'www-data',
  'apache',
  'nginx',
  'mysql',
  'postgres',
  'redis',
  'mongodb',
  'elastic',
  'elasticsearch',

  // Service accounts
  'service',
  'support',
  'helpdesk',
  'security',
  'audit',
  'backup',
  'monitor',
  'prometheus',
  'grafana',

  // Common reserved names
  'api',
  'app',
  'application',
  'config',
  'configuration',
  'test',
  'testing',
  'debug',
  'development',
  'staging',
  'production',
  'prod',
  'dev',
  'stage',

  // Potential social engineering targets
  'ceo',
  'cto',
  'cfo',
  'manager',
  'director',
  'president',
  'executive',
  'board',
  'legal',
  'finance',
  'hr',
  'human-resources',

  // Technical roles that could be targets
  'devops',
  'sysadmin',
  'netadmin',
  'dba',
  'architect',
  'engineer',
  'developer',
  'programmer',
  'analyst',
];

/**
 * Validates username format and security constraints for Aegis management
 *
 * Requirements:
 * - Must be in format "first.last" (e.g., john.doe)
 * - Length between 3 and 32 characters
 * - Only alphabetic characters and dots allowed
 * - Cannot be a reserved system or service username
 * - Both first and last parts must be at least 1 character
 *
 * @param username - Username to validate
 * @returns Validation result with detailed error messages
 */
export const validateUsername = (username: string): ValidationResult => {
  const errors: string[] = [];
  const warnings: string[] = [];

  // Basic format validation
  if (!username || typeof username !== 'string') {
    return {
      isValid: false,
      errors: ['Username is required'],
    };
  }

  // Trim whitespace
  const trimmedUsername = username.trim().toLowerCase();

  // Length validation
  if (trimmedUsername.length < 3) {
    errors.push('Username must be at least 3 characters long');
  }

  if (trimmedUsername.length > 32) {
    errors.push('Username must be no more than 32 characters long');
  }

  // Format validation - must be "first.last"
  const usernamePattern = /^[a-zA-Z]+\.[a-zA-Z]+$/;
  if (!usernamePattern.test(trimmedUsername)) {
    errors.push('Username must be in format "first.last" (e.g., john.doe)');
    errors.push('Only alphabetic characters and one dot are allowed');
  } else {
    // Split and validate parts
    const [first, last] = trimmedUsername.split('.');

    if (first.length === 0) {
      errors.push('First name part cannot be empty');
    }

    if (last.length === 0) {
      errors.push('Last name part cannot be empty');
    }

    // Check for reserved usernames (both full and first part)
    if (RESERVED_USERNAMES.includes(trimmedUsername)) {
      errors.push('Username conflicts with reserved system accounts');
    }

    if (RESERVED_USERNAMES.includes(first)) {
      errors.push(`First name "${first}" conflicts with system accounts`);
    }

    if (RESERVED_USERNAMES.includes(last)) {
      errors.push(`Last name "${last}" conflicts with system accounts`);
    }

    // Additional security checks
    if (first === last) {
      warnings.push(
        'Using the same name for first and last parts may not be ideal'
      );
    }

    // Check for common weak patterns
    if (
      ['test', 'demo', 'example', 'sample'].includes(first) ||
      ['test', 'demo', 'example', 'sample'].includes(last)
    ) {
      warnings.push('Avoid using test, demo, example, or sample in usernames');
    }
  }

  return {
    isValid: errors.length === 0,
    errors,
    warnings: warnings.length > 0 ? warnings : undefined,
  };
};

/**
 * Validates that a username is safe for system use
 * This is a stricter validation that includes additional security checks
 *
 * @param username - Username to validate
 * @returns Validation result with security-focused error messages
 */
export const validateSystemUsername = (username: string): ValidationResult => {
  const baseResult = validateUsername(username);

  if (!baseResult.isValid) {
    return baseResult;
  }

  const trimmedUsername = username.trim().toLowerCase();
  const [first, last] = trimmedUsername.split('.');

  // Additional system-specific checks
  const additionalErrors: string[] = [];
  const additionalWarnings: string[] = [];

  // Check for potential confusion with existing accounts
  if (first.length === 1 || last.length === 1) {
    additionalWarnings.push(
      'Single-letter names may cause confusion with system accounts'
    );
  }

  // Check for numbers (not allowed in basic pattern, but good to be explicit)
  if (/\d/.test(username)) {
    additionalErrors.push('Usernames cannot contain numbers');
  }

  // Check for special characters other than dot
  if (/[^a-zA-Z.]/.test(username)) {
    additionalErrors.push('Usernames can only contain letters and one dot');
  }

  return {
    isValid: baseResult.isValid && additionalErrors.length === 0,
    errors: [...baseResult.errors, ...additionalErrors],
    warnings:
      [...(baseResult.warnings || []), ...additionalWarnings].length > 0
        ? [...(baseResult.warnings || []), ...additionalWarnings]
        : undefined,
  };
};

/**
 * Quick validation check for username format
 * Returns boolean for simple validation scenarios
 *
 * @param username - Username to check
 * @returns True if username passes basic validation
 */
export const isValidUsername = (username: string): boolean => {
  return validateUsername(username).isValid;
};

/**
 * Get user-friendly error message from validation result
 *
 * @param result - Validation result
 * @returns Formatted error message for display
 */
export const getValidationErrorMessage = (result: ValidationResult): string => {
  if (result.isValid) {
    return '';
  }

  if (result.errors.length === 1) {
    return result.errors[0];
  }

  return `Username validation failed:\n${result.errors.map(error => `• ${error}`).join('\n')}`;
};

/**
 * Get user-friendly warning message from validation result
 *
 * @param result - Validation result
 * @returns Formatted warning message for display
 */
export const getValidationWarningMessage = (
  result: ValidationResult
): string => {
  if (!result.warnings || result.warnings.length === 0) {
    return '';
  }

  if (result.warnings.length === 1) {
    return result.warnings[0];
  }

  return `Username warnings:\n${result.warnings.map(warning => `• ${warning}`).join('\n')}`;
};

/**
 * Validates URL format for webhook and integration URLs
 *
 * Requirements:
 * - Must be a valid URL format
 * - Only http:// and https:// protocols allowed
 * - Must have a valid domain
 *
 * @param url - URL string to validate
 * @returns Validation result with detailed error messages
 *
 * @example
 * validateUrl('https://example.com/webhook') // { isValid: true, errors: [] }
 * validateUrl('not-a-url') // { isValid: false, errors: [...] }
 * validateUrl('ftp://example.com') // { isValid: false, errors: ['URL must use http:// or https:// protocol'] }
 */
export const validateUrl = (url: string): ValidationResult => {
  const errors: string[] = [];

  if (!url || typeof url !== 'string') {
    return { isValid: false, errors: ['URL is required'] };
  }

  const trimmed = url.trim();

  if (trimmed === '') {
    return { isValid: false, errors: ['URL is required'] };
  }

  try {
    const parsed = new URL(trimmed);
    // Only allow http and https protocols
    if (!['http:', 'https:'].includes(parsed.protocol)) {
      errors.push('URL must use http:// or https:// protocol');
    }
  } catch {
    errors.push('Please enter a valid URL (e.g., https://example.com)');
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};

/**
 * Quick validation check for URL format
 * Returns boolean for simple validation scenarios
 *
 * @param url - URL to check
 * @returns True if URL passes validation
 */
export const isValidUrl = (url: string): boolean => {
  return validateUrl(url).isValid;
};

/**
 * Type guard to validate if a string is a valid Relationship
 * Used for graph query relationship validation
 *
 * @param label - String to validate
 * @returns True if label is a valid Relationship type
 *
 * @example
 * isValidRelationship('HAS_VULNERABILITY') // true
 * isValidRelationship('IAM_AssumeRole') // true
 * isValidRelationship('INVALID_REL') // false
 */
export function isValidRelationship(label: string): boolean {
  // Check static relationships
  if (
    STATIC_RELATIONSHIPS.includes(
      label as (typeof STATIC_RELATIONSHIPS)[number]
    )
  ) {
    return true;
  }

  // Check prefix relationships
  return RELATIONSHIP_PREFIXES.some((prefix: string) =>
    label.startsWith(prefix)
  );
}

/**
 * Type guard to validate if a string is a valid NodeLabel
 * Used for graph query node label validation
 *
 * @param label - String to validate
 * @returns True if label is a valid NodeLabel type
 *
 * @example
 * isValidNodeLabel('Asset') // true
 * isValidNodeLabel('Risk') // true
 * isValidNodeLabel('INVALID_LABEL') // false
 */
export function isValidNodeLabel(label: string): boolean {
  return NODE_LABEL_VALUES.includes(
    label as (typeof NODE_LABEL_VALUES)[number]
  );
}
